import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import { loadUserPreferences } from '@urc-ui/utils/user-preferences-utils';
import { Sort } from '@urc-ui/store/api/types';
import { DEFAULT_SORT_CONFIG } from '@urc-ui/store/api/subscriptions/subscriptions';
import { DEFAULT_FILTER_STATE } from '@urc-ui/store/constants';

interface SubscriptionsDataTableState {
  readonly pageSize: number;
  readonly preferCacheValue: boolean;
  readonly reportNameSearchTerm: string;
  readonly visibleColumns: string[];
  readonly sortColumn: Sort;
  readonly filters: object;
}

const userPreferences = loadUserPreferences();
const savedPageSize = userPreferences.subTablePageSize.value;
const reportNameSearchTerm = userPreferences.subTableReportNameSearch.value;
const savedColumnState = userPreferences.subTableColumns.value as string[];
let savedSortColumn = DEFAULT_SORT_CONFIG;
let savedFilters = DEFAULT_FILTER_STATE;

savedSortColumn = userPreferences.subTableSort.value;
savedFilters = userPreferences.subTableFilters.value;

const initialState: SubscriptionsDataTableState = {
  pageSize: savedPageSize,
  preferCacheValue: true,
  reportNameSearchTerm: reportNameSearchTerm,
  visibleColumns: savedColumnState,
  sortColumn: savedSortColumn,
  filters: savedFilters
};

/**
 * `createSlice` will infer the state type from the `initialState` argument
 */
export const subscriptionsDataTableSlice = createSlice({
  name: 'subscriptionsDataTableSlice',
  initialState,
  reducers: {
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setPreferCacheValue: (state, action: PayloadAction<boolean>) => {
      state.preferCacheValue = action.payload;
    },
    setReportNameSearchTerm: (state, action: PayloadAction<string>) => {
      state.reportNameSearchTerm = action.payload;
    },
    setVisibleColumns: (state, action: PayloadAction<string[]>) => {
      state.visibleColumns = action.payload;
    },
    setSortColumn: (state, action: PayloadAction<Sort>) => {
      state.sortColumn = action.payload;
    },
    setFilters: (state, action: PayloadAction<object>) => {
      state.filters = action.payload;
    }
  }
});

export const { setPageSize, setPreferCacheValue, setReportNameSearchTerm, setVisibleColumns, setSortColumn, setFilters } =
  subscriptionsDataTableSlice.actions;

// selectors
export const selectPageSize = (state: RootState) => state.subscriptionsDataTable.pageSize;
export const selectPreferCacheValue = (state: RootState) => state.subscriptionsDataTable.preferCacheValue;
export const selectReportNameSearchTerm = (state: RootState) => state.subscriptionsDataTable.reportNameSearchTerm;
export const selectVisibleColumns = (state: RootState) => state.subscriptionsDataTable.visibleColumns;
export const selectSortColumn = (state: RootState) => state.subscriptionsDataTable.sortColumn;
export const selectFilters = (state: RootState) => state.subscriptionsDataTable.filters;

export default subscriptionsDataTableSlice.reducer;
